import React, { useState, useMemo, useEffect } from 'react';
import { ICON_STYLES, FILLED_ICONS, OUTLINE_ICONS, getIconPath, searchIcons } from '../../data/icons';
import './IconPicker.css';

function IconPicker({ isOpen, onClose, onSelect, currentIcon, currentStyle }) {
    const [selectedStyle, setSelectedStyle] = useState(currentStyle || ICON_STYLES.OUTLINE);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedIcon, setSelectedIcon] = useState(currentIcon || null);

    // Filtrer les icônes selon le style et la recherche
    const filteredIcons = useMemo(() => {
        return searchIcons(searchTerm, selectedStyle);
    }, [searchTerm, selectedStyle]);

    // Limiter l'affichage initial pour les performances
    const [displayCount, setDisplayCount] = useState(100);
    const displayedIcons = useMemo(() => {
        return filteredIcons.slice(0, displayCount);
    }, [filteredIcons, displayCount]);

    // Réinitialiser le compteur d'affichage quand on change de style ou de recherche
    useEffect(() => {
        setDisplayCount(100);
    }, [selectedStyle, searchTerm]);

    // Charger plus d'icônes au scroll
    const handleScroll = (e) => {
        const bottom = e.target.scrollHeight - e.target.scrollTop <= e.target.clientHeight + 100;
        if (bottom && displayCount < filteredIcons.length) {
            setDisplayCount(prev => Math.min(prev + 100, filteredIcons.length));
        }
    };

    const handleSelectIcon = (icon) => {
        setSelectedIcon(icon.name);
    };

    const handleConfirm = () => {
        if (selectedIcon) {
            onSelect({
                name: selectedIcon,
                style: selectedStyle,
                path: getIconPath(selectedIcon, selectedStyle)
            });
            onClose();
        }
    };

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="icon-picker-overlay" onClick={handleOverlayClick}>
            <div className="icon-picker-modal">
                <div className="icon-picker-header">
                    <h2>Sélectionner une icône</h2>
                    <button className="icon-picker-close" onClick={onClose}>
                        <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                <div className="icon-picker-toolbar">
                    <div className="icon-picker-search">
                        <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                        <input
                            type="text"
                            placeholder="Rechercher une icône..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        {searchTerm && (
                            <button className="search-clear" onClick={() => setSearchTerm('')}>
                                <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>

                    <div className="icon-picker-style-toggle">
                        <button
                            className={`style-toggle-btn ${selectedStyle === ICON_STYLES.OUTLINE ? 'active' : ''}`}
                            onClick={() => setSelectedStyle(ICON_STYLES.OUTLINE)}
                        >
                            Outline ({OUTLINE_ICONS.length})
                        </button>
                        <button
                            className={`style-toggle-btn ${selectedStyle === ICON_STYLES.FILLED ? 'active' : ''}`}
                            onClick={() => setSelectedStyle(ICON_STYLES.FILLED)}
                        >
                            Filled ({FILLED_ICONS.length})
                        </button>
                    </div>
                </div>

                <div className="icon-picker-info">
                    {searchTerm ? (
                        <p>{filteredIcons.length} icône{filteredIcons.length > 1 ? 's' : ''} trouvée{filteredIcons.length > 1 ? 's' : ''}</p>
                    ) : (
                        <p>Affichage de {displayedIcons.length} / {filteredIcons.length} icônes</p>
                    )}
                </div>

                <div className="icon-picker-grid" onScroll={handleScroll}>
                    {displayedIcons.length === 0 ? (
                        <div className="icon-picker-empty">
                            <svg width="48" height="48" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <p>Aucune icône trouvée</p>
                        </div>
                    ) : (
                        displayedIcons.map((icon) => (
                            <div
                                key={icon.name}
                                className={`icon-picker-item ${selectedIcon === icon.name ? 'selected' : ''}`}
                                onClick={() => handleSelectIcon(icon)}
                                title={icon.label}
                            >
                                <img
                                    src={getIconPath(icon.name, selectedStyle)}
                                    alt={icon.label}
                                    className="icon-preview"
                                />
                                <span className="icon-name">{icon.name}</span>
                            </div>
                        ))
                    )}
                </div>

                <div className="icon-picker-footer">
                    <button className="btn-cancel" onClick={onClose}>
                        Annuler
                    </button>
                    <button
                        className="btn-confirm"
                        onClick={handleConfirm}
                        disabled={!selectedIcon}
                    >
                        Sélectionner
                    </button>
                </div>
            </div>
        </div>
    );
}

export default IconPicker;
